import { useEffect, useMemo, useState } from 'react';
import { Plus, Users, Ruler, ChevronDown, Pencil, Trash2, TrendingUp } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import {
  Page,
  Main,
  Card,
  Button,
  Modal,
  Field,
  LoadingState,
  ErrorState,
  EmptyState,
  inputClass,
} from '@/components/ui';
import { useApi, useMutation } from '@/hooks/useApi';
import api from '@/lib/api';
import { formatDate, cn, todayISO } from '@/lib/utils';
import type { Person, Item } from '@/lib/types';
import { MeasurementChart, type ChartPoint, type ChartMarker } from './components/MeasurementChart';

type Unit = 'in' | 'cm';

interface MeasurementReading {
  id: string;
  value: number;
  unit: Unit;
  measuredOn: string;
}

interface MeasurementsResponse {
  // current = latest reading per measurement name (newest); now includes id.
  current: Record<string, MeasurementReading>;
  // history = all readings per name, NEWEST first.
  history: Record<string, MeasurementReading[]>;
}

const MEASUREMENT_PRESETS = [
  'Chest',
  'Waist',
  'Hips',
  'Shoulders',
  'Sleeve',
  'Neck',
  'Inseam',
  'Outseam',
  'Thigh',
];

export function SizesPage() {
  const { mutate, loading: saving } = useMutation();

  const { data: peopleRes, loading: peopleLoading, error: peopleError, refetch: refetchPeople } =
    useApi<{ people: Person[] }>('/people');
  const people = peopleRes?.people ?? [];

  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedPersonId && people.length > 0) {
      const def = people.find((p) => p.isDefault) ?? people[0];
      setSelectedPersonId(def.id);
    }
  }, [people, selectedPersonId]);

  const {
    data: measurementsRes,
    loading: measurementsLoading,
    refetch: refetchMeasurements,
  } = useApi<MeasurementsResponse>(selectedPersonId ? `/measurements?person_id=${selectedPersonId}` : null);

  const current = measurementsRes?.current ?? {};
  const history = useMemo(() => measurementsRes?.history ?? {}, [measurementsRes]);

  // Bespoke items mark their date on every measurement chart.
  const { data: bespokeRes } = useApi<{ items: Item[] }>('/items?bespoke=1');
  const bespokeMarkers: ChartMarker[] = useMemo(
    () =>
      (bespokeRes?.items ?? [])
        .filter((it) => it.bespokeDate)
        .map((it) => ({ date: it.bespokeDate as string, label: it.name })),
    [bespokeRes],
  );

  // Add person modal
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [personName, setPersonName] = useState('');
  const [personGender, setPersonGender] = useState<'male' | 'female' | 'other'>('male');

  // Add measurement modal (records a new dated reading)
  const [showAddMeasurement, setShowAddMeasurement] = useState(false);
  const [measurementName, setMeasurementName] = useState('');
  const [measurementValue, setMeasurementValue] = useState('');
  const [measurementUnit, setMeasurementUnit] = useState<Unit>('in');
  const [measurementDate, setMeasurementDate] = useState(todayISO());
  const [addError, setAddError] = useState<string | null>(null);

  // Per-name history expansion
  const [expandedName, setExpandedName] = useState<string | null>(null);

  // Edit a single reading
  const [editingReading, setEditingReading] = useState<{ name: string; reading: MeasurementReading } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editUnit, setEditUnit] = useState<Unit>('in');
  const [editDate, setEditDate] = useState(todayISO());
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  // Delete a single reading
  const [deletingReading, setDeletingReading] = useState<{ name: string; reading: MeasurementReading } | null>(null);

  const currentEntries = useMemo(() => Object.entries(current), [current]);

  const handleAddPerson = async () => {
    if (!personName.trim()) return;
    const res = await mutate('post', '/people', {
      name: personName.trim(),
      gender: personGender,
      isDefault: people.length === 0,
    });
    if (res !== null) {
      setShowAddPerson(false);
      setPersonName('');
      setPersonGender('male');
      const created = (res as { person?: Person }).person;
      if (created?.id) setSelectedPersonId(created.id);
      refetchPeople();
    }
  };

  const openAddMeasurement = () => {
    setMeasurementName('');
    setMeasurementValue('');
    setMeasurementUnit('in');
    setMeasurementDate(todayISO());
    setAddError(null);
    setShowAddMeasurement(true);
  };

  const handleAddMeasurement = async () => {
    setAddError(null);
    // The selected person id must be set, otherwise the POST has no owner and
    // the server silently drops it (root cause of "not recorded").
    if (!selectedPersonId) {
      setAddError('Select a person first.');
      return;
    }
    if (!measurementName.trim() || measurementValue === '') return;
    const value = parseFloat(measurementValue);
    if (Number.isNaN(value)) {
      setAddError('Enter a valid number.');
      return;
    }
    // Reject negatives client-side (server rejects too).
    if (value < 0) {
      setAddError('Measurement cannot be negative.');
      return;
    }
    const res = await mutate('post', '/measurements', {
      personId: selectedPersonId,
      name: measurementName.trim(),
      value,
      unit: measurementUnit,
      measuredOn: measurementDate || undefined,
    });
    if (res !== null) {
      setShowAddMeasurement(false);
      setMeasurementName('');
      setMeasurementValue('');
      setMeasurementDate(todayISO());
      // Refetch so the newly added reading shows immediately.
      refetchMeasurements();
    } else {
      setAddError('Could not save measurement. It may be negative or invalid.');
    }
  };

  const openEditReading = (name: string, reading: MeasurementReading) => {
    setEditingReading({ name, reading });
    setEditValue(String(reading.value));
    setEditUnit(reading.unit);
    setEditDate(reading.measuredOn.slice(0, 10));
    setEditError(null);
  };

  const handleSaveReading = async () => {
    if (!editingReading) return;
    setEditError(null);
    const value = parseFloat(editValue);
    if (Number.isNaN(value)) {
      setEditError('Enter a valid number.');
      return;
    }
    if (value < 0) {
      setEditError('Measurement cannot be negative.');
      return;
    }
    setEditSaving(true);
    try {
      // PATCH must use api.patch directly (useMutation only supports post/put/delete).
      await api.patch(`/measurements/${editingReading.reading.id}`, {
        value,
        unit: editUnit,
        measuredOn: editDate || undefined,
      });
      setEditingReading(null);
      refetchMeasurements();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update reading.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteReading = async () => {
    if (!deletingReading) return;
    const res = await mutate('delete', `/measurements/${deletingReading.reading.id}`);
    if (res !== null) {
      setDeletingReading(null);
      refetchMeasurements();
    }
  };

  return (
    <Page>
      <PageHeader section="My Sizes" backHref="/profile" backLabel="Profile" />

      <Main className="space-y-5">
        {peopleLoading && <LoadingState />}
        {peopleError && <ErrorState message={peopleError} />}

        {!peopleLoading && !peopleError && (
          <>
            {/* Person Selector */}
            <div className="flex items-center gap-3">
              <div className="flex-1 flex items-center gap-2 overflow-x-auto">
                {people.map((person) => (
                  <button
                    key={person.id}
                    onClick={() => setSelectedPersonId(person.id)}
                    className={cn(
                      'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all',
                      selectedPersonId === person.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card border border-border text-foreground hover:border-primary/30',
                    )}
                  >
                    {person.name}
                    {person.isDefault && <span className="ml-1 opacity-60">(Default)</span>}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowAddPerson(true)}
                className="p-2 bg-card border border-border rounded-full hover:border-primary/30 transition-all flex-shrink-0"
                aria-label="Add person"
              >
                <Users className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            {people.length === 0 ? (
              <EmptyState
                icon={<Ruler className="w-8 h-8" />}
                title="No people yet"
                description="Add a person to start saving measurements."
                action={
                  <Button onClick={() => setShowAddPerson(true)}>
                    <Plus className="w-4 h-4" />
                    Add Person
                  </Button>
                }
              />
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-foreground">Current Measurements</h2>
                </div>

                {measurementsLoading ? (
                  <LoadingState />
                ) : currentEntries.length === 0 ? (
                  <Card className="p-6 text-center">
                    <p className="text-sm text-muted-foreground">No measurements recorded yet</p>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {currentEntries.map(([name, m]) => {
                      // history is NEWEST first; reverse for an oldest->newest chart.
                      const readings = history[name] ?? [];
                      const chartPoints: ChartPoint[] = [...readings]
                        .reverse()
                        .map((r) => ({ value: r.value, measuredOn: r.measuredOn }));
                      const expanded = expandedName === name;
                      return (
                        <Card key={name} className="overflow-hidden">
                          <div className="px-4 py-3 flex items-center justify-between">
                            <div>
                              <p className="text-sm text-foreground">{name}</p>
                              <p className="text-[10px] text-muted-foreground">
                                Last updated: {formatDate(m.measuredOn)}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-semibold text-foreground">
                                {m.value} {m.unit}
                              </span>
                              {readings.length > 0 && (
                                <button
                                  onClick={() => setExpandedName(expanded ? null : name)}
                                  className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                                  aria-label="Toggle history"
                                >
                                  <ChevronDown
                                    className={cn('w-4 h-4 transition-transform', expanded && 'rotate-180')}
                                  />
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Inline chart only with 2+ readings */}
                          {chartPoints.length >= 2 && (
                            <div className="px-4 pb-3">
                              <MeasurementChart points={chartPoints} unit={m.unit} markers={bespokeMarkers} />
                            </div>
                          )}

                          {expanded && (
                            <div className="border-t border-border">
                              <div className="px-4 py-2 flex items-center gap-2">
                                <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
                                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                  History
                                </h4>
                              </div>
                              <div className="divide-y divide-border">
                                {readings.map((r) => (
                                  <div key={r.id} className="px-4 py-2.5 flex items-center justify-between gap-2">
                                    <div>
                                      <p className="text-sm text-foreground">
                                        {r.value} {r.unit}
                                      </p>
                                      <p className="text-[10px] text-muted-foreground">{formatDate(r.measuredOn)}</p>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      <button
                                        onClick={() => openEditReading(name, r)}
                                        className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                                        aria-label="Edit reading"
                                      >
                                        <Pencil className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={() => setDeletingReading({ name, reading: r })}
                                        className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                                        aria-label="Delete reading"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                )}

                <button
                  onClick={openAddMeasurement}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-card border border-dashed border-border rounded-xl text-sm font-medium text-muted-foreground hover:border-primary/30 hover:text-foreground transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Add or Update Measurement
                </button>
              </>
            )}
          </>
        )}
      </Main>

      {/* Add Person Modal */}
      <Modal
        isOpen={showAddPerson}
        onClose={() => setShowAddPerson(false)}
        title="Add Person"
        footer={
          <Button fullWidth onClick={handleAddPerson} disabled={!personName.trim() || saving}>
            {saving ? 'Adding…' : 'Add Person'}
          </Button>
        }
      >
        <div className="space-y-4">
          <Field label="Name">
            <input
              type="text"
              value={personName}
              onChange={(e) => setPersonName(e.target.value)}
              placeholder="e.g., Sarah, Partner"
              className={inputClass}
            />
          </Field>
          <Field label="Gender (for measurement guides)">
            <div className="flex gap-2">
              {(['male', 'female', 'other'] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setPersonGender(g)}
                  className={cn(
                    'flex-1 py-2.5 rounded-xl text-sm font-medium transition-all',
                    personGender === g ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground',
                  )}
                >
                  {g.charAt(0).toUpperCase() + g.slice(1)}
                </button>
              ))}
            </div>
          </Field>
        </div>
      </Modal>

      {/* Add Measurement Modal */}
      <Modal
        isOpen={showAddMeasurement}
        onClose={() => setShowAddMeasurement(false)}
        title="Add or Update Measurement"
        footer={
          <Button
            fullWidth
            onClick={handleAddMeasurement}
            disabled={!measurementName.trim() || measurementValue === '' || saving}
          >
            {saving ? 'Saving…' : 'Save Measurement'}
          </Button>
        }
      >
        <div className="space-y-4">
          {addError && (
            <div className="rounded-xl bg-destructive/10 text-destructive px-3.5 py-2.5 text-sm">{addError}</div>
          )}
          <Field label="Measurement">
            <input
              type="text"
              value={measurementName}
              onChange={(e) => setMeasurementName(e.target.value)}
              placeholder="e.g., Chest, Waist, Inseam"
              className={inputClass}
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {MEASUREMENT_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setMeasurementName(preset)}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                    measurementName === preset
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-foreground',
                  )}
                >
                  {preset}
                </button>
              ))}
            </div>
          </Field>
          <div className="flex gap-3">
            <div className="flex-1">
              <Field label="Value">
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={measurementValue}
                  onChange={(e) => setMeasurementValue(e.target.value)}
                  placeholder={measurementUnit === 'in' ? 'e.g., 40' : 'e.g., 102'}
                  className={inputClass}
                />
              </Field>
            </div>
            <Field label="Unit">
              <div className="flex gap-1 p-1 bg-secondary rounded-xl">
                {(['in', 'cm'] as const).map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => setMeasurementUnit(u)}
                    className={cn(
                      'px-3 py-2 rounded-lg text-xs font-medium uppercase transition-all',
                      measurementUnit === u ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
                    )}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </Field>
          </div>
          <Field label="Date">
            <input
              type="date"
              value={measurementDate}
              onChange={(e) => setMeasurementDate(e.target.value)}
              className={inputClass}
            />
          </Field>
        </div>
      </Modal>

      {/* Edit Reading Modal */}
      <Modal
        isOpen={editingReading !== null}
        onClose={() => setEditingReading(null)}
        title={`Edit ${editingReading?.name ?? 'Reading'}`}
        footer={
          <Button fullWidth onClick={handleSaveReading} disabled={editValue === '' || editSaving}>
            {editSaving ? 'Saving…' : 'Save Changes'}
          </Button>
        }
      >
        <div className="space-y-4">
          {editError && (
            <div className="rounded-xl bg-destructive/10 text-destructive px-3.5 py-2.5 text-sm">{editError}</div>
          )}
          <div className="flex gap-3">
            <div className="flex-1">
              <Field label="Value">
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className={inputClass}
                />
              </Field>
            </div>
            <Field label="Unit">
              <div className="flex gap-1 p-1 bg-secondary rounded-xl">
                {(['in', 'cm'] as const).map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => setEditUnit(u)}
                    className={cn(
                      'px-3 py-2 rounded-lg text-xs font-medium uppercase transition-all',
                      editUnit === u ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
                    )}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </Field>
          </div>
          <Field label="Date">
            <input
              type="date"
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
              className={inputClass}
            />
          </Field>
        </div>
      </Modal>

      {/* Delete Reading Confirmation */}
      <Modal
        isOpen={deletingReading !== null}
        onClose={() => setDeletingReading(null)}
        title="Delete Reading"
        footer={
          <div className="flex gap-2">
            <Button variant="secondary" fullWidth onClick={() => setDeletingReading(null)}>
              Cancel
            </Button>
            <Button variant="destructive" fullWidth onClick={handleDeleteReading} disabled={saving}>
              {saving ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-foreground">
          Delete the {deletingReading?.name} reading of{' '}
          <span className="font-semibold">
            {deletingReading?.reading.value} {deletingReading?.reading.unit}
          </span>{' '}
          from {deletingReading ? formatDate(deletingReading.reading.measuredOn) : ''}?
        </p>
      </Modal>
    </Page>
  );
}
